/**
 * Corpo da seção "Modo de estudo" em `/revisao`. Invenção da change
 * flashcards-revisao-redesign (proposal.md Suposição 3), não um frame —
 * um frame futuro vence. A ordem da fila é o conjunto fechado da store
 * (`due | random | hardest`): nenhuma spec enumera os valores.
 */
import { ChangeEvent, useId } from "react";
import {
  QUEUE_ORDERS,
  type QueueOrder,
} from "../../../core/store/useRevisaoSelecaoStore";
import { QUEUE_ORDER_LABEL } from "./home/revisaoHome";
import RevisaoFilterChip from "./filtros/RevisaoFilterChip";
import RevisaoFilterChipGroup from "./filtros/RevisaoFilterChipGroup";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "./filtros/tokens";

export interface ModoDeEstudoSecaoProps {
  ahead: boolean;
  onAhead: (ahead: boolean) => void;
  limit: number;
  onLimit: (limit: number) => void;
  queueOrder: QueueOrder;
  onQueueOrder: (queueOrder: QueueOrder) => void;
}

/** Inteiro >= 1. Vazio, NaN, 0, negativo e fração não seguem para o pai. */
function limitAceito(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed < 1) return null;
  return parsed;
}

export default function ModoDeEstudoSecao(props: ModoDeEstudoSecaoProps): JSX.Element {
  const { ahead, onAhead, limit, onLimit, queueOrder, onQueueOrder } = props;
  const limitId = useId();
  const aheadHintId = useId();

  const handleLimit = (event: ChangeEvent<HTMLInputElement>) => {
    const next = limitAceito(event.target.value);
    if (next === null) return;
    onLimit(next);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={ahead}
          aria-describedby={aheadHintId}
          onClick={() => onAhead(!ahead)}
          className={revisaoFilterCx(
            "inline-flex h-9 shrink-0 items-center rounded-chip border px-3.5",
            "text-[13px] font-medium leading-none transition-colors duration-150",
            REVISAO_FILTER_FOCUS_RING,
            ahead
              ? "border-select/[0.42] bg-select/[0.14] text-select-ink"
              : "border-edge bg-canvas text-ink-secondary hover:border-edge-strong hover:bg-surface-hover hover:text-ink",
          )}
        >
          Antecipar
        </button>
        <p id={aheadHintId} className="text-[13px] leading-snug text-ink-secondary">
          Inclui cartões que ainda não venceram.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor={limitId} className="text-[13px] text-ink">
          Limite de cartões da sessão
        </label>
        {/* O número exibido é `limit`. O padrão da store é 20 (DEFAULT_SESSION_LIMIT); quem envia o valor é o pai. */}
        <input
          id={limitId}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={limit}
          onChange={handleLimit}
          className={revisaoFilterCx(
            "h-9 w-24 rounded-input border border-edge bg-canvas px-3 text-sm tabular-nums text-ink",
            REVISAO_FILTER_FOCUS_RING,
          )}
        />
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[13px] text-ink">Ordem da fila</p>
        <RevisaoFilterChipGroup role="radiogroup" ariaLabel="Ordem da fila">
          {QUEUE_ORDERS.map((order) => (
            <RevisaoFilterChip
              key={order}
              role="radio"
              label={QUEUE_ORDER_LABEL[order]}
              selected={queueOrder === order}
              onToggle={() => onQueueOrder(order)}
            />
          ))}
        </RevisaoFilterChipGroup>
      </div>
    </div>
  );
}
